<script setup>
import { computed } from 'vue';
import Draggable from 'vuedraggable';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useDealsStore } from 'dashboard/stores/deals';
import CrmCard from './CrmCard.vue';

const props = defineProps({
  stage: {
    type: Object,
    required: true,
  },
  deals: {
    type: Array,
    default: () => [],
  },
  totals: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['edit', 'delete', 'schedule', 'tasks']);

const { t } = useI18n();
const dealsStore = useDealsStore();

// Each currency in `totals` is formatted and joined on its own — R$ and $
// are never summed together. Empty map (stage has no deals, or all values
// are null) renders nothing in the header.
const formatCurrencyMap = (map = {}) => {
  const parts = Object.entries(map)
    .filter(([, cents]) => cents != null)
    .map(([currency, cents]) =>
      new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
      }).format((cents || 0) / 100)
    );
  return parts.join(' · ');
};

const formattedTotal = computed(() => formatCurrencyMap(props.totals));

// Called on the draggable's `@change`. Only `added` (a card dropped into
// this column from anywhere, including itself) triggers a move — `removed`
// fires on the source column for the same drag and would double-PATCH.
const handleChange = async event => {
  const added = event?.added;
  if (!added) return;

  try {
    await dealsStore.moveToStage(
      added.element.id,
      props.stage.id,
      added.newIndex
    );
  } catch {
    useAlert(t('CRM_FUNIL.MOVE.ERROR'));
  }
};

defineExpose({ handleChange, formattedTotal });
</script>

<template>
  <div class="flex flex-col w-64 shrink-0 bg-n-solid-2 rounded-lg p-2 gap-2">
    <div class="flex items-center justify-between px-1 py-1 gap-2">
      <h3 class="text-sm font-medium text-n-slate-12 truncate">
        {{ stage.name }}
      </h3>
      <div class="flex items-center gap-2 shrink-0">
        <span
          v-if="formattedTotal"
          class="text-xs text-n-slate-10 truncate"
          data-testid="column-total"
        >
          {{ formattedTotal }}
        </span>
        <span class="text-xs text-n-slate-10">{{ deals.length }}</span>
      </div>
    </div>
    <!--
      Always render the Draggable, even for an empty column: vuedraggable/
      Sortable.js only turns an EXISTING DOM container into a drop zone, so
      swapping it out for a `<p>` when `deals` is empty (the old behaviour)
      made empty columns reject drops entirely — you couldn't advance a deal
      into "Em contato" or close it into "Fechado (ganho)"/"Perdido" from a
      freshly seeded board. The empty-state message now lives in the
      `footer` slot, which vuedraggable renders inside the same container
      without being part of the sortable list. `min-h-16` keeps the column
      a real drop target even with zero cards.
    -->
    <Draggable
      :list="deals"
      :group="{ name: 'deals', pull: true, put: true }"
      item-key="id"
      class="flex flex-col gap-2 min-h-16"
      @change="handleChange"
    >
      <template #item="{ element }">
        <CrmCard
          :deal="element"
          @edit="emit('edit', $event)"
          @delete="emit('delete', $event)"
          @schedule="emit('schedule', $event)"
          @tasks="emit('tasks', $event)"
        />
      </template>
      <template #footer>
        <p
          v-if="!deals.length"
          class="text-xs text-n-slate-10 px-1 py-6 text-center"
        >
          {{ t('CRM_FUNIL.STAGE.EMPTY') }}
        </p>
      </template>
    </Draggable>
  </div>
</template>
